import { getUrlData } from '../../../AppHelpers';

const LEAD_INFO_EXPIRY_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// A lead row in Drupal is keyed by sessionId (SessionStoreService::ensureSession
// inserts on an unseen one and bumps visit_number), so a sessionId minted fresh
// on every mount makes a refresh or a second tab look like a second visit by a
// second prospect. Persist it instead, for as long as the tour is plausibly
// still going: same browser, same community, same visit.
const SESSION_ID_KEY_PREFIX = 'tgaiSessionId_';
const SESSION_ID_RANDOM_MAX = 1000;

// How long a persisted id stays resumable with no activity. Long enough to
// cover a real walk-around tour with the phone in a pocket; short enough that
// tomorrow's visit is a new row rather than a continuation of today's. The
// stamp is refreshed on every message, so this is idle time, not total time.
const SESSION_ID_IDLE_EXPIRY_MS = 4 * 60 * 60 * 1000;

export const generateSessionId = () => `session_${Date.now()}${Math.floor(Math.random() * SESSION_ID_RANDOM_MAX)}`;

// Test mode, as TourGuide AI marks it: ?tgai_test=1 on the URL the tester
// opened, made sticky for the tab because the tour navigates client-side and
// the lead form is several steps in.
//
// Deliberately read here rather than imported from ./helpers, whose isTestMode()
// is the authority on these two names: this module is read at reducer-init on
// every tour, and must not depend on a symbol that is present or absent
// depending on which branches have landed. Both names have to stay in step with
// TEST_MODE_PARAM / TEST_MODE_KEY there — that is the whole coupling, and it is
// two strings.
const TEST_MODE_PARAM = 'tgai_test';
const TEST_MODE_KEY = 'tgai_test_mode';

const isTestModeScope = () => {
  try {
    if (new URLSearchParams(window.location.search).get(TEST_MODE_PARAM) === '1') {
      return true;
    }
    return window.sessionStorage.getItem(TEST_MODE_KEY) === '1';
  } catch (error) {
    // Storage blocked. Fall back to the URL alone, which is still right for a
    // tester who has not navigated away from the page they opened. Erring
    // towards "not a test" is the safe direction: it separates nothing that was
    // not already separate, and never merges a test scope into a real one.
    try {
      return new URLSearchParams(window.location.search).get(TEST_MODE_PARAM) === '1';
    } catch (parseError) {
      return false;
    }
  }
};

/**
 * Community slug the persisted session id is scoped to.
 *
 * The same contact touring two communities is deliberately two leads (see
 * TourGuideAiBrochure), so the persisted id must not leak across them. Derived
 * from the URL rather than the config store because it is needed synchronously,
 * at reducer-init time, before the config fetch has resolved.
 *
 * Test runs get their own scope (#630). Drupal records a test session with
 * is_test set, which keeps it off the lead dashboards and out of the CRM, and
 * ensureSession() preserves that flag when a session id it already knows comes
 * back. The two stores have different lifetimes — this one is localStorage,
 * shared across every tab for four hours, while test mode is sessionStorage,
 * sticky to the tab that opened it — so on one shared scope a real prospect in
 * the next tab could resume a session id flagged is_test and reach neither the
 * dashboards nor the CRM, with nothing anywhere to notice.
 */
export const getSessionScope = () => {
  let slug = 'default';
  try {
    const { urlSlug } = getUrlData(window.location.host, window.location);
    slug = urlSlug || 'default';
  } catch (error) {
    slug = 'default';
  }
  return isTestModeScope() ? `test:${slug}` : slug;
};

const sessionIdKey = (scope) => `${SESSION_ID_KEY_PREFIX}${scope || 'default'}`;

export const loadPersistedSessionId = (scope) => {
  try {
    const raw = localStorage.getItem(sessionIdKey(scope));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.sessionId) return null;

    if (!parsed.updatedAt || Date.now() - parsed.updatedAt > SESSION_ID_IDLE_EXPIRY_MS) {
      localStorage.removeItem(sessionIdKey(scope));
      return null;
    }

    return parsed.sessionId;
  } catch (error) {
    return null;
  }
};

export const persistSessionId = (sessionId, scope) => {
  if (!sessionId) return;
  try {
    localStorage.setItem(
      sessionIdKey(scope),
      JSON.stringify({ sessionId, updatedAt: Date.now() }),
    );
  } catch (error) {
    // Storage full or blocked (private mode). A non-persisted id still works;
    // it just loses the refresh/second-tab continuity.
  }
};

export const clearPersistedSessionId = (scope) => {
  try {
    localStorage.removeItem(sessionIdKey(scope));
  } catch (error) {
    // Nothing to do; see persistSessionId.
  }
};

/**
 * The session id this tour should run under: the resumable persisted one when
 * there is one, otherwise a new one.
 */
export const loadOrCreateSessionId = (scope) => (
  loadPersistedSessionId(scope) || generateSessionId()
);

export const loadSavedLeadInfo = () => {
  try {
    const savedData = localStorage.getItem('leadInfo');
    if (!savedData) {
      return null;
    }

    const parsed = JSON.parse(savedData);
    const now = new Date().getTime();

    if (parsed.expiresAt && now > parsed.expiresAt) {
      localStorage.removeItem('leadInfo');
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.error('Error loading saved lead info:', error);
    return null;
  }
};

export const saveLeadInfoToStorage = (leadData) => {
  try {
    const now = new Date().getTime();
    const expiresAt = now + LEAD_INFO_EXPIRY_DAYS * MS_PER_DAY;

    const dataToStore = {
      data: leadData,
      expiresAt,
    };

    localStorage.setItem('leadInfo', JSON.stringify(dataToStore));
  } catch (error) {
    console.error('Error saving lead info to localStorage:', error);
  }
};

export const getFormData = () => {
  // Dev-only. Gated on REACT_APP_IS_PRODUCTION for the same reason as the
  // exclusion override in utils/brochureTracking.js: a build env is easy to set
  // wrong, and without this clause a production build would hand every prospect
  // a form already filled in with someone else's name.
  const shouldPrefill = process.env.REACT_APP_PREFILL_FORM_FOR_TESTING === 'true'
    && process.env.REACT_APP_IS_PRODUCTION !== 'true';

  if (shouldPrefill) {
    return {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '555-123-4567',
    };
  }

  const savedLeadInfo = loadSavedLeadInfo();
  if (savedLeadInfo) {
    return savedLeadInfo;
  }

  return {};
};

const CONVERSATION_KEY_PREFIX = 'chatbot_conversation_';
const ALL_CONVERSATIONS_KEY = 'chatbot_all_conversations';

// Transcripts are retained on the prospect's own device, and nothing in the app
// reads either store back — both are write-only today. So the retention buys no
// product behaviour while carrying a real privacy cost: these copies sit outside
// the server-side redaction gate and outside any deletion request made to us.
//
// Bound: keep at most MAX_STORED_CONVERSATIONS, and nothing older than
// CONVERSATION_MAX_AGE_MS. A session already idles out after four hours
// (SESSION_ID_IDLE_EXPIRY_MS), so a one-day age bound outlives every
// conversation that could still be resumed; the count bound covers a shared tour
// tablet handed between several prospects inside that same day.
const MAX_STORED_CONVERSATIONS = 5;
const CONVERSATION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const conversationKey = (sessionId) => `${CONVERSATION_KEY_PREFIX}${sessionId}`;

// Every chatbot_conversation_* key currently in localStorage, including ones the
// index has lost track of (a write that failed halfway, or an index cleared by an
// older build). Pruning has to work from the real keys, not from the index, or
// orphans are retained forever.
const storedConversationKeys = () => {
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CONVERSATION_KEY_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
};

/**
 * Drop every locally stored transcript.
 *
 * Wired to the "Not you? Click here to start fresh" link, which previously
 * cleared only `leadInfo` and left the transcripts behind — a deletion request
 * that did not delete the thing most worth deleting.
 */
export const clearStoredConversations = () => {
  try {
    storedConversationKeys().forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(ALL_CONVERSATIONS_KEY);
  } catch (error) {
    // Storage blocked; nothing was written in the first place.
  }
};

// Applies the age and count bounds to the index, and deletes the per-session key
// of everything dropped. Returns the retained index.
const pruneConversations = (allConversations) => {
  const now = Date.now();

  // An entry written before savedAt existed has no age; treat it as expired
  // rather than as new, so upgrading prunes the backlog instead of pinning it.
  const withinAge = allConversations.filter(
    (conv) => conv && conv.savedAt && now - conv.savedAt <= CONVERSATION_MAX_AGE_MS,
  );

  // Keep the most recently saved, but preserve the index's existing order so an
  // in-place update does not shuffle entries around.
  let retained = withinAge;
  if (withinAge.length > MAX_STORED_CONVERSATIONS) {
    const keep = new Set(
      [...withinAge]
        .sort((a, b) => b.savedAt - a.savedAt)
        .slice(0, MAX_STORED_CONVERSATIONS)
        .map((conv) => conv.sessionId),
    );
    retained = withinAge.filter((conv) => keep.has(conv.sessionId));
  }

  const retainedIds = new Set(retained.map((conv) => conv.sessionId));
  storedConversationKeys().forEach((key) => {
    if (!retainedIds.has(key.slice(CONVERSATION_KEY_PREFIX.length))) {
      localStorage.removeItem(key);
    }
  });

  return retained;
};

export const saveConversationToStorage = (
  sessionId,
  messageHistory,
  conversationState,
  formData,
  amenityPlaces,
) => {
  const conversationData = {
    sessionId,
    savedAt: Date.now(),
    userInteractions: messageHistory.filter((m) => m.type === 'user').length,
    messages: messageHistory,
    conversationState,
    formData,
    amenityPlaces,
  };

  try {
    let allConversations = JSON.parse(localStorage.getItem(ALL_CONVERSATIONS_KEY) || '[]');
    if (!Array.isArray(allConversations)) {
      allConversations = [];
    }

    const existingIndex = allConversations.findIndex(
      (conv) => conv && conv.sessionId === sessionId,
    );
    if (existingIndex >= 0) {
      allConversations[existingIndex] = conversationData;
    } else {
      allConversations.push(conversationData);
    }

    // Prune before writing, so the bound is enforced on every save rather than
    // only when something else happens to look at the store.
    const retained = pruneConversations(allConversations);

    localStorage.setItem(conversationKey(sessionId), JSON.stringify(conversationData));
    localStorage.setItem(ALL_CONVERSATIONS_KEY, JSON.stringify(retained));
  } catch (error) {
    // Storage full or blocked (private mode). The conversation still runs from
    // React state; only the local copy is lost, and nothing reads it back.
  }
};
